import { z } from "zod";

export enum AppointmentStatus {
  SCHEDULED = "scheduled",
  CHECKED_IN = "checked_in",
  COMPLETED = "completed",
  CANCELLED = "cancelled",
  NO_SHOW = "no_show",
  RESCHEDULED = "rescheduled",
  UNKNOWN = "unknown",
}

export enum AppointmentType {
  INITIAL_CONSULTATION = "initial_consultation",
  FOLLOW_UP = "follow_up",
  PROCEDURE = "procedure",
  ROUTINE_CHECKUP = "routine_checkup",
  URGENT_CARE = "urgent_care",
  TELEHEALTH = "telehealth",
  SPECIALIST_CONSULT = "specialist_consult",
  INJECTION = "injection",
  NEW_PATIENT_CHECKIN = "new_patient_checkin",
  EXISTING_PATIENT_CHECKIN = "existing_patient_checkin",
  // A known patient visit whose source type does not distinguish new vs. established patients.
  // This is a read category, not an implicit EHR booking type.
  NEW_OR_EXISTING_PATIENT_CHECKIN = "new_or_existing_patient_checkin",
  RESEARCH = "research_visit",
  // Used to map for appointments that are not known to the system or are not really appointments/catch-all for all other cases.
  UNKNOWN = "unknown",
}

/** Canonical model fields shared by stored appointments and scheduling projections. */
export const appointmentDetailsSchema = z.object({
  type: z.nativeEnum(AppointmentType),
  status: z.nativeEnum(AppointmentStatus).nullable(),
  ehrReason: z.string().nullable(),
  durationMinutes: z.number().int().positive().nullish(),
});

// The scheduling actions the agent can take on an appointment. Used by the scope check
// (getRelevantSchedulingRules) to evaluate a group's action-scoped rules — e.g. a group may
// allow rescheduling a follow-up but prohibit cancelling a surgery.
export enum SchedulingAction {
  Schedule = "schedule",
  Reschedule = "reschedule",
  Cancel = "cancel",
  Confirm = "confirm",
}

// A semantic category identified by the scheduling-scope service primarily from the appointment
// reason. This is intentionally separate from AppointmentType: EHR appointment-type mappings are
// lossy, while the reason can distinguish an operative procedure from CT, sleep, testing, etc.
export enum AppointmentScopeCategory {
  // Keep the original wire value for backend/LiveKit rolling-deploy compatibility.
  SurgeryOrProcedure = "procedure",
  Other = "other",
}

const INACTIVE_APPOINTMENT_STATUSES: ReadonlySet<AppointmentStatus> = new Set([
  AppointmentStatus.CANCELLED,
  AppointmentStatus.COMPLETED,
  AppointmentStatus.NO_SHOW,
  AppointmentStatus.RESCHEDULED,
]);

/** Whether an appointment still occupies its slot. A missing status counts as active. */
export function isActiveAppointmentStatus(status: AppointmentStatus | null | undefined): boolean {
  return !status || !INACTIVE_APPOINTMENT_STATUSES.has(status);
}

/**
 * Whether `action` is permitted on an appointment carrying the given per-appointment `allowedActions`
 * (VAL-2214). `undefined` means the group advertises no per-appointment restriction, so the action is
 * allowed (the agent keeps its default flow). Otherwise the action must be listed.
 */
export function appointmentAllowsAction(
  allowedActions: SchedulingAction[] | null | undefined,
  action: SchedulingAction
): boolean {
  return !allowedActions || allowedActions.includes(action);
}

export const pendingAppointmentStatuses: AppointmentStatus[] = [
  AppointmentStatus.SCHEDULED,
  AppointmentStatus.CHECKED_IN,
];

export const attendedAppointmentStatuses: AppointmentStatus[] = [
  AppointmentStatus.COMPLETED,
  AppointmentStatus.CHECKED_IN,
];

export const reschedulableStatuses = [...pendingAppointmentStatuses, AppointmentStatus.CANCELLED];
// used to see if the patient has been scheduled with the provider once before
export const onceBookedStatuses = [
  ...new Set([...pendingAppointmentStatuses, ...attendedAppointmentStatuses, ...reschedulableStatuses]),
];
